import { OpenIDProvider } from '@salte-auth/salte-auth';
import { injectable, Container, LazyServiceIdentifer, inject, unmanaged } from "inversify";
import { injectToken, getToken, Token, TokenContainerModule } from "inversify-token";
import { YinzCamConfigServer, YinzCamConfigServerToken } from "yinzcam-config";
import { YinzCamInjectModule } from 'yinzcam-inject';
import { JanusModeContextManager, JanusModeContextManagerToken } from "./mode";
import { get, Readable, writable, Writable } from "svelte/store";
import { YinzCamSignonServer, YinzCamSignonServerToken } from 'yinzcam-signon';
import { YinzCamDSPServerToken, YinzCamDSPServer } from 'yinzcam-signon';
import { AbstractReactiveMicrocomponent, ControlBase, DeadlineTimer, ManualPassthrough, OutputMapper, ReactiveMicrocomponent, ReactiveMicrocomponentConfig, SimpleComponent, SimpleSink } from 'yinzcam-rma';
import { YinzCamAPIRequestParameterComponent, YinzCamAPIRequestParameterComponentToken, YinzCamAPIRequestParameters, YinzCamAPIResponse } from 'yinzcam-api';
import { ycSSOAuthFinished, ycTicket } from './ycapp';
import { SalteAuth } from '@salte-auth/salte-auth';
import { Tab } from '@salte-auth/tab';
import Axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
//import { YinzCamDropsServer, YinzCamDropsServerToken } from 'yinzcam-drops';
// TODO: Move this into a PGA server
import { YinzCamAppServer, YinzCamAppServerToken } from 'yinzcam-appserver';
import _ from "lodash";
import { asyncCallWithTimeout, promiseWithResolvers } from './utilities';
import { tick } from 'svelte';

export const JanusSignonManagerToken = new Token<JanusSignonManager>(Symbol.for("JanusSignonManager"));

const JanusSignonStateManagerToken = new Token<JanusSignonStateManager>(Symbol.for("JanusSignonStateManager"));

export let JanusSignonManagerModule: YinzCamInjectModule = new YinzCamInjectModule((container: Container): void => {
  container.load(new TokenContainerModule((bindToken) => {
    bindToken(JanusSignonStateManagerToken).to(JanusSignonStateManager).inSingletonScope();
    bindToken(JanusSignonManagerToken).to(JanusSignonManager).inSingletonScope();
    bindToken(YinzCamAPIRequestParameterComponentToken).to(JanusSignonManagerParameterComponent).inSingletonScope();
  }));
});

export interface SSOResult {
  success: boolean;
  data?: any;
  errorCode?: string;
  errorMessage?: { 
    title: string; 
    description: string
  };
}

export interface SignonStatus {
  loggedIn: boolean;
  ticket?: string;
  expiration?: number;
  validSpan?: number;
  yinzId?: string;
  accountCreated?: boolean;
  dropsTicket?: string;
  serviceTicket?: string;
};

const INTERNAL_ERROR: SSOResult = {
  success: false,
  errorCode: 'INTERNAL_ERROR',
  errorMessage: {
    title: 'Internal Error',
    description: 'An internal error occurred.'
  }
};

// this is stored outside of the manager class to avoid circular dependencies with the parameter component
@injectable()
class JanusSignonStateManager {
  private readonly statusComponent: ManualPassthrough<SignonStatus>;
  private readonly expirationTimeComponent: ReactiveMicrocomponent<number>;
  private readonly expirationTimerComponent: ReactiveMicrocomponent<number>;
  private readonly expirationHandlerComponent: ReactiveMicrocomponent<void>;

  public constructor() {
    this.statusComponent = new ManualPassthrough({ name: 'JanusSignonStatusComponent', saveToLocalStorage: !CONFIG.ssoDiscardLoginStateAtEndOfSession }, { loggedIn: false });
    this.expirationTimeComponent = new OutputMapper(
      { name: 'JanusSignonStatusExpirationTimeComponent', saveToLocalStorage: false }, this.statusComponent,
      ($status) => {
        if (!$status?.loggedIn || !$status.expiration) {
          return 0x7FFFFFFF;
        }
        // give 60 second buffer before real end of ticket lifetime
        return $status.expiration - 60*1000;
      });
    this.expirationTimerComponent = new DeadlineTimer('JanusSignonStatusExpirationTimerComponent', this.expirationTimeComponent);
    this.expirationHandlerComponent = new SimpleSink(
      { name: 'JanusSignonStatusExpirationHandlerComponent', saveToLocalStorage: false },
      async ($timer: number) => {
        if (!$timer) {
          return;
        }
        console.log('JanusSignonStatusExpirationHandlerComponent: timer fired, logging out (IGNORED)');
        //this.statusComponent.setValue({ loggedIn: false });
      }, this.expirationTimerComponent);    
  }

  public getStatusComponent(): ReactiveMicrocomponent<SignonStatus> {
    return this.statusComponent;
  }

  public getStatus(): SignonStatus {
    return this.statusComponent.getValue();
  }

  public updateTicketStatus(status: SignonStatus) {
    if (!status?.loggedIn || !status?.ticket || !status?.expiration) {
      console.log('JanusSignonStateManager.updateTicketStatus: not logged in or invalid ticket, setting logged out');
      this.statusComponent.setValue({ loggedIn: false });
      return;
    }
    // for testing expiration logout
    //status.expiration = Date.now() + 90000;
    console.log('JanusSignonStateManager: status updated', status);
    this.statusComponent.setValue(status);
  }
}

@injectable()
export class JanusSignonManager {
  private readonly appId: string;
  private userStateChanged: boolean;
  private urlToLaunchAfterEnd: string = null;

  public skipCompleteProfilePage: boolean;

  public constructor (
    @injectToken(JanusModeContextManagerToken) private readonly modeManager: JanusModeContextManager,
    @injectToken(YinzCamSignonServerToken) private readonly signonServer: YinzCamSignonServer,
    @injectToken(YinzCamDSPServerToken) private readonly dspServer: YinzCamDSPServer,
    @injectToken(YinzCamConfigServerToken) private readonly configServer: YinzCamConfigServer,
    @injectToken(JanusSignonStateManagerToken) private readonly stateManager: JanusSignonStateManager,
    //@injectToken(YinzCamDropsServerToken) private readonly dropServer: YinzCamDropsServer,
    @injectToken(YinzCamAppServerToken) private readonly appServer: YinzCamAppServer) {
    this.appId = "venues" === CONFIG.league.toLowerCase() ? `venue_${CONFIG.tricode}`.toUpperCase() : `${CONFIG.league}_${CONFIG.tricode}`.toUpperCase();
    this.userStateChanged = false;

    (ycTicket as Readable<{ticket: string, validSpan: number}>).subscribe((ti) => {
      if (!ti || !ti.ticket) {
        console.log('JanusSignonManager: bad update ticket status call', ti, ti?.ticket, ti?.validSpan);
        return;
      }
      if (!ti.validSpan) {
        ti.validSpan = 31556926000;
      }
      console.log('JanusSignonManager: updating ticket status from ycapp', ti.ticket, ti.validSpan);
      setTimeout(async () => {
        this.stateManager.updateTicketStatus({
          loggedIn: true,
          ticket: ti.ticket,
          expiration: Date.now() + ti.validSpan,
          validSpan: ti.validSpan,
          //dropsTicket: await this.getDropTicket(ti.ticket),
          //serviceTicket: await this.getServiceTicket(ti.ticket)
        });
      }, 0);
    });
  }

  private async updateTicketStatus(status: SignonStatus) {
    this.stateManager.updateTicketStatus({
      ...status,
      //dropsTicket: await this.getDropTicket(status.ticket),
      //serviceTicket: await this.getServiceTicket(status.ticket)
    });
  }

  //Alumni chat
  private async getServiceTicket(ssoTicket: string) {
    const res = await this.appServer.getTicket(ssoTicket);
    console.log('getServiceTicket: ', res);
    return (<any>res?.data)?.Ticket;
  }

  // TODO: REMOVE THIS FROM HERE!!
  /*
  private async getDropTicket(ssoTicket: string) {
    let data = {"UMS":{"Ticket":`${ssoTicket}`}};
    let dropTicket: any = await this.dropServer.getDropTicket(data).then(res => res?.data);
    console.log('dropTicket: ', dropTicket);
    //this.dropServer.setDropTicket(dropTicket);
    return dropTicket?.Ticket;
  }
  */

  public getStatusComponent(): ReactiveMicrocomponent<SignonStatus> {
    return this.stateManager.getStatusComponent();
  }

  public getStatus(): SignonStatus {
    return this.stateManager.getStatus();
  }

  public isUserStateChanged(): boolean {
    return this.userStateChanged;
  }

  public getUrlToLaunchAfterEnd(): string {
    return this.urlToLaunchAfterEnd;
  }

  public setUrlToLaunchAfterEnd(url: string) {
    this.urlToLaunchAfterEnd = url;
  }

  public logout(): SignonStatus {
    this.updateTicketStatus(null);
    return this.stateManager.getStatus();
  }

  public addWorkflowEndListener(listener: () => void) {
    window.addEventListener('yc_sso_workflow_end', listener, false);
  }

  public fireWorkflowEndEvent() {
    window.dispatchEvent(new Event('yc_sso_workflow_end'));
  }

  /*
  <TicketResponse>
	<Ticket>+n2I1dH3Q22T0Zb85VSbZwAAAXnjpnikhFEO+itsfbW4U9pa/X4VQcgpxS5RkRL7grCGAr61s1DIyziZP2o0xjH92dVZK0atCB/Wjy2mh20C/zb0OkOTq6JpOZBq/oXSXeM0UFNDS+UPdJZIdLijDpodtRFGk/3a</Ticket>
	<IssueTime>1623022008484</IssueTime>
	<ExpireTime>1626622008484</ExpireTime>
	<ValidSpan>3600000000</ValidSpan>
	<YinzId>b8b1eff1-f91b-4872-ad43-e0c98b3d131a</YinzId>
	<AccountCreated>true</AccountCreated>
</TicketResponse>
*/

  /*
  <RestErrorResponse>
	<ErrorCode>DSP_SERVER_ERROR</ErrorCode>
	<UserMessage>
		<Title>Registration Error</Title>
		<Content>Looks like you've already signed up with this email.  Try logging in with your email and password.</Content>
	</UserMessage>
</RestErrorResponse>
*/
  private makeErrorResult(data: any): SSOResult {
    if (data?.ErrorCode) {
      // TODO: make this a common utility function
      return {
        success: false,
        data,
        errorCode: data.ErrorCode,
        errorMessage: {
          title: data.UserMessage?.Title || 'Error',
          description: data.UserMessage?.Content || 'An error occurred.'
        }
      }
    } else {
      return { ...INTERNAL_ERROR, data };
    }
  }

  private async handleTicketGrantingAPIResponse(rsp: YinzCamAPIResponse): Promise<SSOResult> {
    if (!rsp?.data) {
      return INTERNAL_ERROR;
    }
    let data = rsp.data as any;
    if (data.Ticket && data.ExpireTime) {
      this.updateTicketStatus({
        loggedIn: true,
        ticket: data.Ticket,
        expiration: parseInt(data.ExpireTime),
        validSpan: parseInt(data.ValidSpan),
        yinzId: data.YinzId,
        accountCreated: data.AccountCreated === 'true'
      });
      this.userStateChanged = true;
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          resolve({ success: true, data });
        }, 0);
      });
    } else {
      return this.makeErrorResult(data);
    }
  }

  public async registerWithDSPUserPassword(user: string, password: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postRegister({
      Credentials: {
        DSPAccount: {
          AppId: this.appId,
          ClientId: CONFIG.dspClientId,
          DeviceId: await this.getDSPGuestId(),
          Username: user,
          Password: password
        }
      },
      Profile: {
        Entry: [{
          Key: 'locale',
          Value: navigator.language
        }]
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async registerWithYinzCamEmailPassword(email: string, password: string, profile?: { [key: string]: string }): Promise<SSOResult> {
    let entries = (profile)? Object.entries(profile).filter(e => !_.isNil(e[1])).map(e => ({ Key: e[0], Value: e[1] })) : {};
    entries["email"] = email;
    let rsp = await this.signonServer.postRegister({
      Credentials: {
        AppId: this.appId,
        Identity: email,
        Passphrase: password
      },
      Profile: {
        Entry: entries
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  private createMapFromProfileResponse(data: any): { [key: string]: string } {
    if (!data?.Entry) {
      return {};
    }
    let entries = data.Entry as { Key: string, Value: string}[];
    let dict = Object.fromEntries(entries.map(e => [e.Key, e.Value])); 
    return dict;
  }

  /*
  private async waitForTicket(): Promise<string> {
    const p = promiseWithResolvers<string>();
    const u = this.getStatusComponent().store.subscribe((status) => {
      try {
        alert('GOT STATUS UPDATE' + JSON.stringify(status));
        if (status.ticket) {
          p.resolve(status.ticket);
        }  
      } catch (e) {
        alert('EXCEPTION WAITING FOR UPDATE');
      }
    });
    try {
      return asyncCallWithTimeout(p.promise, 5000);
    } finally {
      u();
    }
  }
  */

  private async getProfileSegment(segment: string): Promise<SSOResult> {
    let rsp = { isStatusNot2xx: true, data: {} };
    for (let i = 0; i < 5; i++) {
      try {
        rsp = await this.signonServer.getProfile2UserSegment(segment);
      } catch (e) {
        console.log('caught exception waiting for profile segment response', e);
      }
      if (rsp && !rsp.isStatusNot2xx) {
        break;
      }
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
    let data = rsp?.data as any;
    return { success: !rsp.isStatusNot2xx, data: this.createMapFromProfileResponse(data) };
  }

  private getProfileSegmentComponent(segment: string): ReactiveMicrocomponent<{ [key: string]: string }> {
    let ic = this.signonServer.getProfile2UserSegmentComponent('DSPACCOUNT');
    let name = `sso:getProfileSegmentComponent:${segment}`;
    return new OutputMapper({ name, saveToLocalStorage: false }, ic, (rsp) => {
      return this.createMapFromProfileResponse(rsp?.data);
    });
  }

  public getProfileSegmentStore(segment: string): Readable<{ [key: string]: string }> {
    return this.getProfileSegmentComponent(segment).store;
  }

  private async updateProfileSegment(segment: string, profile: { [key: string]: string }): Promise<SSOResult> {
    let entries = Object.entries(profile).filter(e => !_.isNil(e[1])).map(e => ({ Key: e[0], Value: e[1] }));
    let data = { Entry: entries };
    let rsp = await this.signonServer.postProfile2UserSegment(segment, data);
    data = rsp?.data as any;
    if (!data.Entry) {
      return this.makeErrorResult(data);
    }
    this.userStateChanged = true;
    return { success: true };
  }

  public async updateUser(profile: { [key: string]: string }): Promise<SSOResult> {
    // let entries = Object.entries(profile).map(e => ({ Key: e[0], Value: e[1] }));
    // let data = { Entry: entries };
    let rsp = await this.signonServer.postUser(this.getStatus().ticket, profile);
    const data = rsp?.data as any;
    if (!data.Entry) {
      return this.makeErrorResult(data);
    }
    this.userStateChanged = true;
    return { success: true };
  }

  public async getTokens(segment: string): Promise<SSOResult> {
    let rsp = { isStatusNot2xx: true, data: {} };
    for (let i = 0; i < 5; i++) {
      try {
        rsp = await this.signonServer.getTokens(segment);
      } catch (e) {
        console.log('caught exception waiting for tokens response', e);
      }
      if (rsp && !rsp.isStatusNot2xx) {
        break;
      }
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
    let data = rsp?.data as any;
    return { success: !rsp.isStatusNot2xx, data };
  }

  public async getCustomNbaIdUnlink(segment: string): Promise<SSOResult> {
    const rsp = await this.signonServer.getCustomNbaIdUnlink(segment);
    return { success: !rsp.isStatusNot2xx, data: rsp.data };
  }

  public async loginWithYinzCamUserPassword(user: string, password: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postTicket({
      YinzCam: {
        AppId: this.appId,
        Identity: user,
        Passphrase: password
      },
      CreateNewAccount: false
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async loginWithFacebookAccount(accessToken: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postLink({
      Facebook: {
        AppId: this.appId,
        AccessToken: accessToken
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async loginWithGoogleAccount(authorizationCode: string, accessToken: string, refreshToken: string, idToken: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postTicket({
      Google: {
        AppId: this.appId,
        AuthorizationCode: authorizationCode,
        AccessToken: accessToken,
        RefreshToken: refreshToken,
        IDToken: idToken
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async loginWithAppleAccount(clientId: string, clientData: string, authorizationCode: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postLink({
      Apple: {
        AppId: this.appId,
        ClientId: clientId,
        ClientData: clientData,
        AuthorizationCode: authorizationCode
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async loginWithDjangoEmailPassword(email: string, password: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postTicket({
      Django: {
        AppId: this.appId,
        Email: email,
        Password: password
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async loginWithSportsAllianceEmailPassword(email: string, password: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postTicket({
      SportsAlliance: {
        AppId: this.appId,
        Username: email,
        Email: email,
        Password: password,
        Language: navigator.language?.toLowerCase().split("-", 2)[0]
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async loginWithSportsAllianceSocialAccount(socialProvider: string, socialToken: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postTicket({
      SportsAlliance: {
        AppId: this.appId,
        SocialProvider: socialProvider,
        SocialToken: socialToken,
        Language: navigator.language?.toLowerCase().split("-", 2)[0]
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async loginWithSportsAllianceTokenPayload(tokenPayload: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postTicket({
      SportsAlliance: {
        AppId: this.appId,
        TokenPayload: tokenPayload
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async loginWithSportsAllianceTokens(clientId: string, scope: string, redirectUri: string, code: string, codeVerifier: string, appIdOverride?: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postTicket({
      SportsAlliance: {
        AppId: appIdOverride || this.appId,
        ClientId: clientId,
        Scope: scope,
        RedirectUri: redirectUri,
        Code: code, 
        CodeVerifier: codeVerifier,
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async loginWithFMFEmailPassword(username: string, password: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postTicket({
      FMFFanId: {
        AppId: this.appId,
        Username: username,
        Password: password
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async loginWithLow6UserPassword(user: string, password: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postTicket({
      Low6: {
        AppId: this.appId,
        Username: user,
        Password: password
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async loginWithGigya(uid: string, uidSignature: string, signatureTimestamp: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postTicket({
      Gigya: {
        AppId: this.appId,
        UID: uid,
        UIDSignature: uidSignature,
        SignatureTimestamp: signatureTimestamp
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async loginWithNFLID(uid: string, uidSignature: string, signatureTimestamp: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postTicket({
      NFLID: {
        AppId: this.appId,
        UID: uid,
        UIDSignature: uidSignature,
        SignatureTimestamp: signatureTimestamp
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async loginWithNFLIDOpenID(clientId: string, scope: string, redirectUri: string, code: string, codeVerifier: string, usePing: boolean): Promise<SSOResult> {
    let rsp = await this.signonServer.postTicket({
      NFLID: {
        AppId: this.appId,
        ClientId: clientId,
        Scope: scope,
        RedirectUri: redirectUri,
        Code: code,
        CodeVerifier: codeVerifier,
        UsePing: usePing,
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async loginWithNBAID(authorizationCode: string, codeVerifier: string, redirectUri: string, appIdOverride?: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postTicket({
      NBAID: {
        AppId: appIdOverride || this.appId,
        AuthorizationCode: authorizationCode,
        CodeVerifier: codeVerifier,
        RedirectUri: redirectUri
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async loginWithFMFFanKey(userToken: string, redirectUri: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postTicket({
      FMFFanKey: {
        AppId: this.appId,
        UserToken: userToken,
        RedirectUri: redirectUri
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async loginWithPing(authorizationCode: string, codeVerifier: string, redirectUri: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postTicket({
      Ping: {
        AppId: this.appId,
        AuthorizationCode: authorizationCode,
        CodeVerifier: codeVerifier,
        RedirectUri: redirectUri
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async loginWithTunespeak(authCode: string, codeVerifier: string, redirectUri: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postTicket({
      Tunespeak: {
        AppId: this.appId,
        AuthorizationCode: authCode, /* server calls this authCode internally but the API field uses AuthorizationCode */
        CodeVerifier: codeVerifier,
        RedirectUri: redirectUri
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async loginWithEnactor(authorizationCode: string, redirectUri: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postTicket({
      Enactor: {
        AppId: this.appId,
        AuthorizationCode: authorizationCode,
        RedirectUri: redirectUri
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async loginWithAuth0AuthorizationCode(authorizationCode: string, codeVerifier: string, redirectUri: string, appIdOverride?: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postTicket({
      Auth0: {
        AppId: appIdOverride || this.appId,
        AuthorizationCode: authorizationCode,
        CodeVerifier: codeVerifier,
        RedirectUri: redirectUri
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async loginWithCognitoAuthorizationCode(authorizationCode: string, codeVerifier: string, redirectUri: string, appIdOverride?: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postTicket({
      AWSCognito: {
        AppId: appIdOverride || this.appId,
        AuthorizationCode: authorizationCode,
        CodeVerifier: codeVerifier,
        RedirectUri: redirectUri
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async loginWithCognitoAccessToken(accessToken: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postTicket({
      AWSCognito: {
        AppId: this.appId,
        AccessToken: accessToken
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async loginWithTicketmasterAccount(accessToken: string, forceLink: boolean, appIdOverride?: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postTicket({
      TicketmasterArchtics: {
        AppId: appIdOverride || this.appId,
        AccessToken: accessToken
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }
  
  public async loginWithAxsAccount(accessToken: string, forceLink: boolean): Promise<SSOResult> {
      let rsp = await this.signonServer.postTicket({
        AXS: {
          AppId: this.appId,
          AccessToken: accessToken
        }
      });
      return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async loginWithSeatGeekAccount(accessToken: string, forceLink: boolean): Promise<SSOResult> {
    let rsp = await this.signonServer.postTicket({
      SeatGeek: {
        AppId: this.appId,
        AccessToken: accessToken
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async registerWithLow6UserPassword(user: string, password: string, profile: { [key: string]: string }): Promise<SSOResult> {
    let rsp = await this.signonServer.postRegister( {
      Credentials: {
        Low6: {
          AppId: this.appId,
          Username: user,

          Password: password
        }
      },
      Profile: { Entry: Object.entries(profile).map(e => ({ Key: e[0], Value: e[1] })) }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async registerWithSportsAllianceEmailPassword(email: string, password: string, profile: { [key: string]: string }): Promise<SSOResult> {
    let rsp = await this.signonServer.postRegister( {
      Credentials: {
        SportsAlliance: {
          AppId: this.appId,
          Username: email,
          Email: email,
          Password: password,
          Language: navigator.language?.toLowerCase().split("-", 2)[0]
        }
      },
      Profile: { Entry: Object.entries(profile).map(e => ({ Key: e[0], Value: e[1] })) }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async registerWithFMFEmailPassword(username: string, password: string, profile: { [key: string]: string }): Promise<SSOResult> {
    let rsp = await this.signonServer.postRegister( {
      Credentials: {
        FMFFanId: {
          AppId: this.appId,
          Username: username,
          Password: password
        }
      },
      Profile: { Entry: Object.entries(profile).map(e => ({ Key: e[0], Value: e[1] })) }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async resetWithLow6UserPassword(user: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postPasswordReset(this.getStatus().ticket, {
        Low6: {
          AppId: this.appId,
          Username: user,
        }
    });
    
    if (!rsp || rsp.isStatusNot2xx) {
      return this.makeErrorResult(rsp?.data);
    }
    return { success: true };
  }

  public async linkWithTicketmasterAccount(accessToken: string, forceLink: boolean, appIdOverride?: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postLink({
      ForceLink: forceLink,
      TicketmasterArchtics: {
        AppId: appIdOverride || this.appId,
        AccessToken: accessToken
      }
    });
    
    if (!rsp || rsp.isStatusNot2xx) {
      return this.makeErrorResult(rsp?.data);
    }
    this.userStateChanged = true;
    return { success: true };
  }

  public async linkWithSeatGeekAccount(accessToken: string, forceLink: boolean): Promise<SSOResult> {
    let rsp = await this.signonServer.postLink({
      ForceLink: forceLink,
      SeatGeek: {
        AppId: this.appId,
        AccessToken: accessToken
      }
    });
    
    if (!rsp || rsp.isStatusNot2xx) {
      return this.makeErrorResult(rsp?.data);
    }
    this.userStateChanged = true;
    return { success: true };
  }

  public async linkWithFacebookAccount(accessToken: string, forceLink: boolean): Promise<SSOResult> {
    let rsp = await this.signonServer.postLink({
      ForceLink: forceLink,
      Facebook: {
        AppId: this.appId,
        AccessToken: accessToken
      }
    });
    
    if (!rsp || rsp.isStatusNot2xx) {
      return this.makeErrorResult(rsp?.data);
    }
    this.userStateChanged = true;
    return { success: true };
  }

  public async linkWithGoogleAccount(authorizationCode: string, accessToken: string, refreshToken: string, idToken: string, forceLink: boolean): Promise<SSOResult> {
    let rsp = await this.signonServer.postLink({
      ForceLink: forceLink,
      Facebook: {
        AppId: this.appId,
        AuthorizationCode: authorizationCode,
        AccessToken: accessToken,
        RefreshToken: refreshToken,
        IDToken: idToken
      }
    });
    
    if (!rsp || rsp.isStatusNot2xx) {
      return this.makeErrorResult(rsp?.data);
    }
    this.userStateChanged = true;
    return { success: true };
  }

  public async linkWithAppleAccount(clientId: string, clientData: string, authorizationCode: string, forceLink: boolean): Promise<SSOResult> {
    let rsp = await this.signonServer.postLink({
      ForceLink: forceLink,
      Apple: {
        AppId: this.appId,
        ClientId: clientId,
        ClientData: clientData,
        AuthorizationCode: authorizationCode
      }
    });
    
    if (!rsp || rsp.isStatusNot2xx) {
      return this.makeErrorResult(rsp?.data);
    }
    this.userStateChanged = true;
    return { success: true };
  }

  public async linkWithClearAccount(authCode: string, callbackUrl: string, forceLink: boolean): Promise<SSOResult> {
    let rsp = await this.signonServer.postLink({
      ForceLink: forceLink,
      Clear: {
        AppId: this.appId,
        AuthCode: authCode,
        CallbackUrl: callbackUrl
      }
    });
    
    if (!rsp || rsp.isStatusNot2xx) {
      return this.makeErrorResult(rsp?.data);
    }
    this.userStateChanged = true;
    return { success: true };
  }

  public async linkWithAXSAccount(accessToken: string, forceLink: boolean): Promise<SSOResult> {
    let rsp = await this.signonServer.postLink({
      ForceLink: forceLink,
      AXS: {
        AppId: this.appId,
        AccessToken: accessToken
      }
    });
    
    if (!rsp || rsp.isStatusNot2xx) {
      return this.makeErrorResult(rsp?.data);
    }
    this.userStateChanged = true;
    return { success: true };
  }

  public async linkWithNFLIDAccount(uid: string, uidSignature: string, signatureTimestamp: string, forceLink: boolean): Promise<SSOResult> {
    let rsp = await this.signonServer.postLink({
      ForceLink: forceLink,
      NFLID: {
        AppId: this.appId,
        UID: uid,
        UIDSignature: uidSignature,
        SignatureTimestamp: signatureTimestamp
      }
    });
    
    if (!rsp || rsp.isStatusNot2xx) {
      return this.makeErrorResult(rsp?.data);
    }
    this.userStateChanged = true;
    return { success: true };
  }

  public async linkWithNFLIDOpenIDAccount(clientId: string, scope: string, redirectUri: string, code: string, codeVerifier: string, usePing: boolean, forceLink: boolean): Promise<SSOResult> {
    let rsp = await this.signonServer.postLink({
      ForceLink: forceLink,
      NFLID: {
        AppId: this.appId,
        ClientId: clientId,
        Scope: scope,
        RedirectUri: redirectUri,
        Code: code,
        CodeVerifier: codeVerifier,
        UsePing: usePing,
      }
    });
    
    if (!rsp || rsp.isStatusNot2xx) {
      return this.makeErrorResult(rsp?.data);
    }
    this.userStateChanged = true;
    return { success: true };
  }

  public async linkWithParkWhizAccount(code: string, redirectUri: string, forceLink: boolean): Promise<SSOResult> {
    let rsp = await this.signonServer.postLink({
      ForceLink: forceLink,
      ParkWhiz: {
        AppId: this.appId,
        Code: code,
        RedirectUri: redirectUri
      }
    });
    
    if (!rsp || rsp.isStatusNot2xx) {
      return this.makeErrorResult(rsp?.data);
    }
    this.userStateChanged = true;
    return { success: true };
  }

  public async unlinkWithTicketmasterAccount(appIdOverride?: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postUnlink({
      Type: 'TICKETMASTER_ARCHTICS',
      AppId: appIdOverride || this.appId,
    });    
    if (!rsp || rsp.isStatusNot2xx) {
      return this.makeErrorResult(rsp?.data);
    }
    this.userStateChanged = true;
    return { success: true };
  }

  public async unlinkWithFacebookAccount(): Promise<SSOResult> {
    let rsp = await this.signonServer.postUnlink({
      Type: 'FACEBOOK',
      AppId: this.appId,
    });    
    if (!rsp || rsp.isStatusNot2xx) {
      return this.makeErrorResult(rsp?.data);
    }
    this.userStateChanged = true;
    return { success: true };
  }

  public async unlinkWithGoogleAccount(): Promise<SSOResult> {
    let rsp = await this.signonServer.postUnlink({
      Type: 'GOOGLE',
      AppId: this.appId,
    });    
    if (!rsp || rsp.isStatusNot2xx) {
      return this.makeErrorResult(rsp?.data);
    }
    this.userStateChanged = true;
    return { success: true };
  }

  public async unlinkWithAppleAccount(): Promise<SSOResult> {
    let rsp = await this.signonServer.postUnlink({
      Type: 'APPLE',
      AppId: this.appId,
    });    
    if (!rsp || rsp.isStatusNot2xx) {
      return this.makeErrorResult(rsp?.data);
    }
    this.userStateChanged = true;
    return { success: true };
  }

  public async unlinkWithSeatGeekAccount(appIdOverride?: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postUnlink({
      Type: 'SEATGEEK',
      AppId: appIdOverride || this.appId,
    });    
    if (!rsp || rsp.isStatusNot2xx) {
      return this.makeErrorResult(rsp?.data);
    }
    this.userStateChanged = true;
    return { success: true };
  }

  public async unlinkWithClearAccount(): Promise<SSOResult> {
    let rsp = await this.signonServer.postUnlink({
      Type: 'CLEAR',
      AppId: this.appId,
    });    
    if (!rsp || rsp.isStatusNot2xx) {
      return this.makeErrorResult(rsp?.data);
    }
    this.userStateChanged = true;
    return { success: true };
  }

  public async unlinkWithAXSAccount(appIdOverride?: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postUnlink({
      Type: 'AXS',
      AppId: appIdOverride || this.appId,
    });    
    if (!rsp || rsp.isStatusNot2xx) {
      return this.makeErrorResult(rsp?.data);
    }
    this.userStateChanged = true;
    return { success: true };
  }

  public async unlinkWithNFLIDAccount(): Promise<SSOResult> {
    let rsp = await this.signonServer.postUnlink({
      Type: 'NFLID',
      AppId: this.appId,
    });    
    if (!rsp || rsp.isStatusNot2xx) {
      return this.makeErrorResult(rsp?.data);
    }
    this.userStateChanged = true;
    return { success: true };
  }

  public async unlinkWithParkWhizAccount(): Promise<SSOResult> {
    let rsp = await this.signonServer.postUnlink({
      Type: 'PARKWHIZ',
      AppId: this.appId,
    });    
    if (!rsp || rsp.isStatusNot2xx) {
      return this.makeErrorResult(rsp?.data);
    }
    this.userStateChanged = true;
    return { success: true };
  }

  public getDSPProfileSegment(): Promise<SSOResult> {
    return this.getProfileSegment('DSPACCOUNT');
  }

  private getDefaultProfileSegmentName(): string {
    return CONFIG.defaultProfileSegment || 'USER';
  }

  public getUserProfileSegment(segmentOverride: string = null): Promise<SSOResult> {
    return this.getProfileSegment(segmentOverride || this.getDefaultProfileSegmentName());
  }

  public getDSPProfileSegmentComponent(): ReactiveMicrocomponent<{ [key: string]: string }> {
    return this.getProfileSegmentComponent('DSPACCOUNT');
  }

  public async checkDSPAccountVerification(): Promise<SSOResult> {
    let res = await this.getDSPProfileSegment();
    if (!res?.success) {
      return res;
    }
    return { success: res.data?.['verified'] === 'true' };
  }

  public updateDSPProfileSegment(profile: { [key: string]: string }): Promise<SSOResult> {
    return this.updateProfileSegment('DSPACCOUNT', profile);
  }

  public updateUserProfileSegment(profile: { [key: string]: string }): Promise<SSOResult> {
    return this.updateProfileSegment(this.getDefaultProfileSegmentName(), profile);
  }

  public async uploadDSPProfileImage(image: Blob, name: string): Promise<SSOResult> {
    let formData = new FormData();
    formData.append('file', image, name);
    let rsp = await this.signonServer.postUploadImageSegment('DSPAccount', formData);
    //console.log('uploadDSPProfileImage', rsp);
    return { success: true, data: rsp.data };
  }

  public async uploadUserProfileImage(image: Blob, name: string): Promise<SSOResult> {
    let formData = new FormData();
    formData.append('file', image, name);
    console.log('file: ', formData);
    let rsp = await this.signonServer.postUploadImageSegment(this.getDefaultProfileSegmentName(), formData);
    return { success: true, data: rsp.data };
  }

  public async loginWithDSPUserPassword(user: string, password: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postTicket({
      DSPAccount: {
        AppId: this.appId,
        ClientId: CONFIG.dspClientId,
        DeviceId: await this.getDSPGuestId(),
        Username: user,
        Password: password
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async loginWithDSPAccessToken(accessToken: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postTicket({
      DSPAccount: {
        AppId: this.appId,
        ClientId: CONFIG.dspClientId,
        DeviceId: await this.getDSPGuestId(),
        AccessToken: accessToken
      }
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  private createDSPSocialAuth(policy: string): SalteAuth {
    return new SalteAuth({      
      providers: [
        new LaLigaDSPProvider({
          baseURL: 'https://prelogin.laliga.es',
          tenantID: 'LaLigaDspPreB2C.onmicrosoft.com',
          clientID: CONFIG.dspClientId,
          policy,
        })
      ],
      handlers: [ new Tab({ default: true }) ]
    });
  }

  private async doDSPSocialLogin(policy: string): Promise<SSOResult> {
    const auth = this.createDSPSocialAuth(policy);
    const result = await new Promise<SalteAuth.EventWrapper>((resolve, reject) => {
      auth.on('login', (error, data) => {
        if (error) {
          reject(error);
        } else {
          resolve(data);
        }
      });
      auth.login('laliga-dsp');
    });
    const provider = auth.provider(result.provider) as LaLigaDSPProvider;
    const token = await provider.exchangeCodeForToken();
    return this.loginWithDSPAccessToken(token);
  }

  public async loginWithDSPFacebook(): Promise<SSOResult> {
    return this.doDSPSocialLogin('b2c_1a_signupsignin_fb_nl');
  }

  public async loginWithDSPGoogle(): Promise<SSOResult> {
    return this.doDSPSocialLogin('b2c_1a_signupsignin_gl_nl');
  }

  public async loginWithRayadosUserPassword(email: string, password: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postTicket({
      Rayados: {
        AppId: this.appId,
        Email: email,
        Password: password
      },
      CreateNewAccount: false
    });
    return this.handleTicketGrantingAPIResponse(rsp);
  }

  public async passwordResetWithDSP(user: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postPasswordReset(this.getStatus().ticket, {
      DSPAccount: {
        AppId: this.appId,
        ClientId: CONFIG.dspClientId,
        DeviceId: await this.getDSPGuestId(),
        Username: user
      }
    });
    if (!rsp || rsp.isStatusNot2xx) {
      return this.makeErrorResult(rsp?.data);
    }
    return { success: true };
  }

  public async passwordResetBegin(email: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postPasswordResetBegin({ Identity: email });
    console.log('rsp: ', rsp)
    if (!rsp || rsp.isStatusNot2xx) {
      return this.handleGrantResponseForNot2xx(rsp);
    }
    return { success: true };
  }

  public async passwordResetValidate(email: string, pin: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postPasswordResetValidate({ Identity: email, PIN: pin });
    console.log('rsp: ', rsp)
    if (!rsp || rsp.isStatusNot2xx) {
      return this.handleGrantResponseForNot2xx(rsp);
    }
    return { success: true };
  }

  public async passwordResetEnd(email: string, pin: string, password: string): Promise<SSOResult> {
    let rsp = await this.signonServer.postPasswordResetEnd({ Identity: email, PIN: pin, Passphrase: password });
    if (!rsp || rsp.isStatusNot2xx) {
      return this.handleGrantResponseForNot2xx(rsp);
    }
    return { success: true };
  }

  public handleGrantResponseForNot2xx(rsp: YinzCamAPIResponse) {
    const data = rsp?.data as any;
    return {
      success: false,
      data,
      errorCode: data.ErrorCode,
      errorMessage: {
        title: data.UserMessage?.Title || 'Error',
        description: data.UserMessage?.Content || 'An error occurred.'
      }
    }
  }

  public getConsentsWithStatus(): ReactiveMicrocomponent<YinzCamAPIResponse> {
    // get or load guest ID
    //let guestId = await this.getDSPGuestId();

    // get consents from config server
    let configLegalComp = this.configServer.getConfigLegal();
    //console.log('GET CONSENTS', rsp);

    // wrap with our current consent status
    let consentStatusComp = new ConsentsWithStatusComponent({ name: 'JanusSignonManager.getConsentsWithStatus', saveToLocalStorage: false }, configLegalComp);

    return consentStatusComp;
  }

  // TODO: probably want this class to manage consentData?
  // this version of the function works with profile fields instead of consent IDs
  public async setConsentStatusOld(consentFlags: { [profileField: string]: boolean }, consentData: { version: string, consents: { id: string, profileField: string}[] }) {
    // store locally
    let consentStatus = { consentsVersion: consentData.version, consentFlags };
    localStorage.setItem('JanusSignonManager:ConsentStatusJson', JSON.stringify(consentStatus));

    // get our guest ID
    let dspGuestId = await this.getDSPGuestId();

    // accept policy
    await this.dspServer.postPoliciesAccept(dspGuestId, consentData.version, new Date());

    // accept consents
    // have to jump through all these hoops because "id" isn't always present on consents...
    let idConsentFlags = Object.fromEntries(consentData.consents.filter(c => c.id).map(c => [ c.id, consentFlags[c.profileField] || false ]));
    await this.dspServer.postConsentsStatus(dspGuestId, idConsentFlags);
  }

  public async setConsentStatus(consentFlags: { [consentId: string]: boolean }, consentData: { version: string, consents: { id: string, profileField: string}[] }) {
    // store locally
    let consentStatus = { consentsVersion: consentData.version, consentFlags };
    localStorage.setItem('JanusSignonManager:ConsentStatusJson', JSON.stringify(consentStatus));

    // get our guest ID
    let dspGuestId = await this.getDSPGuestId();

    try {
      // accept policy
      await this.dspServer.postPoliciesAccept(dspGuestId, consentData.version, new Date());

      // accept consents
      // have to jump through all these hoops because "id" isn't always present on consents...
      await this.dspServer.postConsentsStatus(dspGuestId, consentFlags);
    } catch (e) {
      console.log("Unable to save consents", e);
    }
  }

  // I'm not sure if this even needs to be used?
  public async getDSPGuestId() {
    let guestId = localStorage.getItem('JanusSignonManager:DSPGuestId');
    if (!guestId) {
      // TODO: get country code from some API
      let countryCode = 'es';

      // Request a new guest ID from DSP
      let rsp = await this.dspServer.postRegisterGuest(countryCode, this.appId);
      guestId = (rsp.data as any).guestId;
      localStorage.setItem('JanusSignonManager:DSPGuestId', guestId);
    }
    // Store it in a cookie because LaLiga said so.
    let dt = new Date();
    dt.setTime(dt.getTime() + (365*24*60*60*1000));
    let expires = "; expires=" + dt.toUTCString();
    let hostname = window.location.hostname;
    document.cookie = "_guid=" + guestId + expires + "; domain=" + hostname;
    return guestId;
  }

  public async subscriptionUpdateWithStripe(productId: string, priceId: string, workflowState: string, setupIntentId: string=undefined, paymentIntentId: string=undefined): Promise<SSOResult> {
    let rsp = await this.signonServer.postSubscriptionUpdate({
      ReceiptInfo: {
        Service: 'STRIPE',
        Stripe: {
          ProductId: productId,
          PriceId: priceId,
          WorkflowState: workflowState,
          SetupIntentId: setupIntentId,
          PaymentIntentId: paymentIntentId
        }
      }
    });
    if (!rsp || rsp.isStatusNot2xx) {
      return this.handleGrantResponseForNot2xx(rsp);
    }
    this.userStateChanged = true;
    return { success: true, data: rsp.data };
  }

  private randomCode(): string {
    let array = new Uint8Array(32);
    array = globalThis.crypto.getRandomValues(array);
    return String.fromCharCode.apply(null, Array.from(array));
  }

  private base64URLEncode(str: string): string {
    const b64 = btoa(str);
    const encoded = b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
    return encoded;
  }

  public generatePKCECodeVerifier(): string {
    const rando = this.randomCode();
    const encoded = this.base64URLEncode(rando);
    console.log(`Code Verifier strings random: ${rando} | encoded: ${encoded}`);
    return encoded;
  }

  private async sha256(str: string): Promise<string>{
    const digestOp = await globalThis.crypto.subtle.digest(
      { name: "SHA-256" },
      new TextEncoder().encode(str)
    );
    return this.bufferToBase64UrlEncoded(digestOp);
  };
  
  private bufferToBase64UrlEncoded(hash: ArrayBuffer): string {
    const uintArray = new Uint8Array(hash);
    const numberArray = Array.from(uintArray);
    const hashString = String.fromCharCode(...numberArray);
    return this.urlEncodeB64(btoa(hashString));
  };
  
  private urlEncodeB64(input: string) {
    const b64Chars: { [index: string]: string } = { "+": "-", "/": "_", "=": "" };
    return input.replace(/[+/=]/g, (m: string) => b64Chars[m]);
  };
  
  public async generatePKCEChallenge(codeVerifierString: string): Promise<string> {
    const sha = await this.sha256(codeVerifierString);
    console.log(`generated sha ${sha} for codeVerifier ${codeVerifierString}`);
    return sha;
  }

  public async exchangeCodeForToken(tokenUrl: string, clientId: string, scope: string, redirectUri: string, code: string, codeVerifier?: string): Promise<Record<string, string>> {
    const params = new URLSearchParams();
    params.append('grant_type', 'authorization_code');
    params.append('client_id', clientId);
    params.append('scope', scope);
    params.append('redirect_uri', redirectUri);
    params.append('code', code);
    if (codeVerifier) {
      params.append('code_verifier', codeVerifier);
    }
    const config = {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    };
    const result = await Axios.post(tokenUrl, params, config);
    return result?.data;
  }

  public async getCustomFMFFanKeyAuthenticate() {
    return (await this.signonServer.getCustomFMFFanKeyAuthenticate()).data as any;
  }
}

// Modifies a consent response to include local status info
class ConsentsWithStatusComponent extends AbstractReactiveMicrocomponent<YinzCamAPIResponse, [YinzCamAPIResponse]> {
  public constructor(config: ReactiveMicrocomponentConfig<any>, input: ReactiveMicrocomponent<YinzCamAPIResponse>) {
    super(config, input);
  }

  protected async update($control: unknown, $input: YinzCamAPIResponse): Promise<YinzCamAPIResponse> {
    let consentStatusJson = localStorage.getItem('JanusSignonManager:ConsentStatusJson');
    let consentStatus = { consentsVersion: "0.0", consentFlags: {} };
    if (consentStatusJson) {
      consentStatus = JSON.parse(consentStatusJson);
    }
    ($input.data as any).consentStatus = consentStatus;
    return $input;
  }
}

@injectable()
export class JanusSignonManagerParameterComponent extends AbstractReactiveMicrocomponent<YinzCamAPIRequestParameters, [ SignonStatus ]> implements YinzCamAPIRequestParameterComponent {
  public constructor(@injectToken(JanusSignonStateManagerToken) private readonly stateManager: JanusSignonStateManager) {
    super({ name: 'JanusSignonManagerParameterComponent', saveToLocalStorage: false }, stateManager.getStatusComponent());
  }

  protected async update($control: unknown, $status: SignonStatus): Promise<YinzCamAPIRequestParameters> {
    if (!$status?.loggedIn || !$status?.ticket) {
      return {};
    }
    //console.log("JanusSignonManagerParameterComponent dropsTicket: " + $status.dropsTicket);
    let ret: YinzCamAPIRequestParameters = {
      services: {
        signon: {
          headers: {
            'X-YinzCam-Ticket': $status.ticket
          }
        }
      }
    };
    /*
    if ($status.dropsTicket) {
      ret.services.app = {
        headers: {
          'X-YinzCam-Ticket': $status.dropsTicket
        }
      }
    }
    */
    if ($status.serviceTicket) {
      ret.services.app = {
        headers: {
          'X-YinzCam-Ticket': $status.serviceTicket
        }
      }
    }
    return ret;
  }
}

export interface LaLigaDSP {
  config: LaLigaDSP.Config;
}

export declare namespace LaLigaDSP {
  interface Config extends OpenIDProvider.Config {
    baseURL?: string;
    tenantID: string;
    policy: string;
  }
}

export class LaLigaDSPProvider extends OpenIDProvider {
  constructor(config: LaLigaDSP.Config) {
    // fill in defaults
    config = {
      baseURL: 'https://login.microsoftonline.com',
      responseType: 'code',
      redirectUrl: new URL('/', window.location.origin).toString().slice(0, -1),
      ...config
    };
    // compute optional fields, if they are missing
    config = {
      url: `${config.baseURL}/${config.tenantID}/oauth2/v2.0`,
      scope: `openid ${config.clientID} offline_access`,
      ...config
    };
    super(config);
  }

  get name() {
    return 'laliga-dsp';
  }

  get login() {
    return this.url(`${this.config.url}/authorize`, {
      p: this.config.policy,
    });
  }

  get logout() {
    return this.url(`${this.config.url}/logout`, {
      p: this.config.policy,
    });
  }

  get token() {
    return this.url(`${this.config.url}/token`, {
      p: this.config.policy,
    });
  }

  public async exchangeCodeForToken(): Promise<string> {
    if (!this.code) {
      throw new Error('code must be available, call login first');
    }
    throw new Error("not implemented");
    //return this.exchangeCodeForToken2(this.token, this.config.clientID, this.config.scope, this.code);
  }
}